import { serverTimestamp } from "firebase/firestore";
import { Asserts, ObjectSchema } from "yup";
import schema from "@/schema";
import { getCurrentUser } from "@/services/auth";
import { TLicenciadaDatabaseFields, TLicenciadaForm } from "../firestore";

const MIN_NAME_LENGTH = 3;

export const estadosData: { label: string; value: TEstado }[] = [
  { value: "AC", label: "Acre" },
  { value: "AL", label: "Alagoas" },
  { value: "AP", label: "Amapá" },
  { value: "AM", label: "Amazonas" },
  { value: "BA", label: "Bahia" },
  { value: "CE", label: "Ceará" },
  { value: "DF", label: "Distrito Federal" },
  { value: "ES", label: "Espírito Santo" },
  { value: "GO", label: "Goías" },
  { value: "MA", label: "Maranhão" },
  { value: "MT", label: "Mato Grosso" },
  { value: "MS", label: "Mato Grosso do Sul" },
  { value: "MG", label: "Minas Gerais" },
  { value: "PA", label: "Pará" },
  { value: "PB", label: "Paraíba" },
  { value: "PR", label: "Paraná" },
  { value: "PE", label: "Pernambuco" },
  { value: "PI", label: "Piauí" },
  { value: "RJ", label: "Rio de Janeiro" },
  { value: "RN", label: "Rio Grande do Norte" },
  { value: "RS", label: "Rio Grande do Sul" },
  { value: "RO", label: "Rondônia" },
  { value: "RR", label: "Roraíma" },
  { value: "SC", label: "Santa Catarina" },
  { value: "SP", label: "São Paulo" },
  { value: "SE", label: "Sergipe" },
  { value: "TO", label: "Tocantins" }
];

const responsavelSchema = schema.object({
  nome: schema.string().trim().min(MIN_NAME_LENGTH).required().defined().default("").label("Nome"),
  email: schema.string().trim().email().required().defined().default("").label("E-mail"),
  telefone: schema.string().trim().phone().required().defined().default("").label("Telefone")
});

const enderecoSchema: ObjectSchema<TLicenciada["endereco"]> = schema.object({
  cep: schema.string().trim().required().defined().default("").label("CEP"),
  logradouro: schema.string().trim().required().defined().default("").label("Logradouro"),
  numero: schema.string().trim().required().defined().default("").label("Número"),
  complemento: schema.string().trim().nullable().defined().default("").label("Complemento"),
  bairro: schema.string().trim().required().defined().default("").label("Bairro"),
  estado: schema
    .mixed<TEstado>()
    .oneOf(estadosData.map((estado) => estado.value))
    .required()
    .defined()
    .label("Estado"),
  cidade: schema.string().trim().required().defined().default("").label("Cidade")
});

const acessosSchema: ObjectSchema<TLicenciada["acessos"]> = schema.object({
  admin: schema.number().required().defined().default(0).label("Admin"),
  member: schema.number().required().defined().default(0).label("Member"),
  team: schema.number().required().defined().default(0).label("Team"),
  master: schema.number().required().defined().default(0).label("Master"),
  collaborator: schema.number().required().defined().default(0).label("Collaborator")
});

const autorizacoesSchema: ObjectSchema<TLicenciada["autorizacoes"]> = schema.object({
  gestao: schema.boolean().required().defined().default(false).label("Gestão"),
  diagnostico: schema.boolean().required().defined().default(false).label("Diagnóstico"),
  incentivo: schema.boolean().required().defined().default(false).label("Incentivo"),
  configuracoes: schema.boolean().required().defined().default(false).label("Configurações")
});

/**
 * Define only the meta fields that are not part of the form
 */
const licenciadaSchemaDatabaseFields: ObjectSchema<TLicenciadaDatabaseFields> = schema.object().shape({
  createdAt: schema
    .mixed<ReturnType<typeof serverTimestamp>>()
    .required()
    .defined()
    .nonNullable()
    .default<ReturnType<typeof serverTimestamp>>(() => serverTimestamp()),
  createdBy: schema
    .string()
    .required()
    .defined()
    .nonNullable()
    .default(() => getCurrentUser()?.uid ?? ""),
  updatedAt: schema
    .mixed<ReturnType<typeof serverTimestamp>>()
    .default<ReturnType<typeof serverTimestamp>>(() => serverTimestamp()),
  updatedBy: schema.string().default(() => getCurrentUser()?.uid ?? ""),
  deletedAt: schema.mixed<ReturnType<typeof serverTimestamp>>().defined().nullable().default(null)
});

/**
 * Define the schema for the form
 */
const licenciadaSchemaFormFields = schema.object({
  id: schema.string().optional(),
  refPath: schema.string().optional(),
  cnpj: schema.string().trim().cnpj().required().defined().default("").label("CNPJ"),
  razaoSocial: schema.string().trim().min(MIN_NAME_LENGTH).required().defined().default("").label("Razão Social"),
  nomeFantasia: schema.string().trim().min(MIN_NAME_LENGTH).required().defined().default("").label("Nome Fantasia"),
  email: schema.string().trim().email().required().defined().default("").label("E-mail"),
  telefone: schema.string().trim().phone().required().defined().default("").label("Telefone"),
  status: schema.string().trim().required().defined().default("ATIVO").label("Status"),
  contratoInicio: schema.date().required().defined().label("Início do Contrato"),
  contratoFim: schema.date().required().defined().label("Fim do Contrato"),
  responsavel: responsavelSchema.required().defined().label("Responsável"),
  endereco: enderecoSchema.required().defined().label("Endereço"),
  acessos: acessosSchema.required().defined().label("Acessos"),
  autorizacoes: autorizacoesSchema.required().defined().label("Autorizações"),
  theme: schema
    .object({
      primary: schema.string().trim().optional().default(""),
      primary1: schema.string().trim().optional().default(""),
      primary6: schema.string().trim().optional().default(""),
      primary7: schema.string().trim().optional().default(""),
      primary8: schema.string().trim().optional().default(""),
      secondary: schema.string().trim().optional().default(""),
      secondary4: schema.string().trim().optional().default(""),
      accent: schema.string().trim().optional().default(""),
      primaryAlt: schema.string().trim().optional().default(""),
      surface: schema.string().trim().optional().default(""),
      surfaceMuted: schema.string().trim().optional().default(""),
      surfaceSoft: schema.string().trim().optional().default(""),
      textSecondary: schema.string().trim().optional().default(""),
      textMuted: schema.string().trim().optional().default(""),
      borderMuted: schema.string().trim().optional().default(""),
      textStrong: schema.string().trim().optional().default(""),
      black: schema.string().trim().optional().default(""),
      white: schema.string().trim().optional().default(""),
      success: schema.string().trim().optional().default(""),
      successAlt: schema.string().trim().optional().default(""),
      warning: schema.string().trim().optional().default(""),
      danger: schema.string().trim().optional().default(""),
      neutral: schema.string().trim().optional().default(""),
      background: schema.string().trim().optional().default(""),
      premiumAqua: schema.string().trim().optional().default(""),
      premiumPink: schema.string().trim().optional().default(""),
      premiumOrange: schema.string().trim().optional().default(""),
      tooltip: schema.string().trim().optional().default("")
    })
    .optional()
    .default({})
    .label("Tema")
});

const licenciadaSchema: ObjectSchema<TLicenciadaForm> = licenciadaSchemaFormFields
  // eslint-disable-next-line unicorn/prefer-spread
  .concat(licenciadaSchemaDatabaseFields)
  .noUnknown()
  .strict()
  .label("Licenciada");

export type LicenciadaInputs = Asserts<typeof licenciadaSchema>;

export default licenciadaSchema;
